'user client';

import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import styled from 'styled-components';

import { isBreakpoint, device } from '@/ui/styles/breakpoints';
import { addLocalePath } from '@/utils/urlBuilder/utils';
import { setHash } from '@/utils/hashChange';

const ContentsWrap = styled.div``;

type IsActiveProps = {
  $isActive?: boolean;
};

const Li = styled.li<IsActiveProps>`
  display: list-item;
  counter-increment: step-counter;
  padding: 0.325rem 0rem 0.325rem 0.125rem;
  align-items: flex-start;
  gap: 0.625rem;
  align-self: stretch;
  list-style-position: inside;
  color: ${props => (props.$isActive ? '#E05D2D' : '#6a6a6a')};
  font-weight: ${props => (props.$isActive ? '700' : '400')};

  &::before {
    content: counter(step-counter) '. ';
    color: ${props => (props.$isActive ? '#E05D2D' : '#6a6a6a')};
    font-weight: ${props => (props.$isActive ? '700' : '400')};
    font-family: 'Atlas Grotesk';
    font-size: 0.8125rem;
  }

  a {
    cursor: pointer;
    color: ${props => (props.$isActive ? '#E05D2D' : '#6a6a6a')};
    font-weight: ${props => (props.$isActive ? '700' : '400')};
    font-family: 'Atlas Grotesk';
    font-size: 0.8125rem;
    font-style: normal;
    line-height: 1.25rem; /* 153.846% */

    &:hover {
      text-decoration: underline;
    }
  }
`;

const GuideItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-top: 1px solid #dedede;
  padding: 0.625rem 0;

  &:hover {
    text-decoration: underline;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const NumberedList = styled.ol<IsActiveProps>`
  list-style: none;
  counter-reset: step-counter;
  margin: 0;
  padding: 0.6rem 0 0.6rem 0.3rem;
  border-top: 1px solid #dedede;
`;

export default function Contents({ activeStep, guide, categories, siblings, steps, updateSidebar, sidebar }) {
  const locale = useLocale();
  const router = useRouter();
  const stepRefs = useRef(steps.map(() => React.createRef()));

  // Initialize with the current guide expanded
  const [expandedGuides, setExpandedGuides] = useState(() => {
    const expanded = {};
    siblings.forEach(sibling => {
      expanded[sibling.id] = sibling.id === guide.id;
    });
    return expanded;
  });

  const toggleGuide = id => {
    setExpandedGuides(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (sidebar) {
      if (isBreakpoint(device.min.lg)) {
        if (stepRefs.current[activeStep] && stepRefs.current[activeStep].current) {
          stepRefs.current[activeStep].current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }
    }
  }, [activeStep, stepRefs, sidebar]);

  return (
    <ContentsWrap>
      <h4>{categories.flatMap(c => c.childrens).find(ch => ch?.slug === guide.categories[0]).title}</h4>
      <List>
        {siblings.map(sibling => (
          <li key={sibling.id}>
            {guide.id === sibling.id ? (
              <>
                <GuideItem onClick={() => toggleGuide(sibling.id)}>
                  <strong>{guide.translations_info[locale].object_title}</strong>
                </GuideItem>
                {expandedGuides[sibling.id] && (
                  <NumberedList>
                    {steps.map((s, i) => (
                      <Li $isActive={s.id === steps[activeStep].id} key={s.id} ref={stepRefs.current[i]}>
                        <a
                          onClick={() => {
                            setHash(s.id.toString());
                            if (isBreakpoint(device.max.md)) {
                              updateSidebar(null);
                            }
                          }}
                        >
                          {s.title}
                        </a>
                      </Li>
                    ))}
                  </NumberedList>
                )}
              </>
            ) : (
              <>
                <GuideItem onClick={() => toggleGuide(sibling.id)}>
                  <span>{sibling.title}</span>
                </GuideItem>
                {/* Show steps for other guides when expanded */}
                {expandedGuides[sibling.id] && sibling.steps && (
                  <NumberedList>
                    {sibling.steps.map(s => (
                      <Li key={s.id} $isActive={s.id === steps[activeStep].id}>
                        <a
                          onClick={() => {
                            router.push(addLocalePath(locale, `guide/${sibling?.slug}${s.id ? `#${s.id}` : ''}`));
                            if (isBreakpoint(device.max.md)) {
                              updateSidebar(null);
                            }
                          }}
                        >
                          {s.title}
                        </a>
                      </Li>
                    ))}
                  </NumberedList>
                )}
              </>
            )}
          </li>
        ))}
      </List>
    </ContentsWrap>
  );
}
